import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { introBridge, introPoll, introReward, peerLabel, references, unmetNeedHeadline } from '../data/teaser';
import { PollBlock } from './PollBlock';
interface QuizOverlayProps {
  onClose: () => void;
  /** Storyboard only — renders a specific state of the quiz. */
  previewState?: 'question' | 'result';
}
const EASE = [0.23, 1, 0.32, 1] as const;
const DOTS = Array.from({
  length: 100
}, (_, i) => i);
const HIGHLIGHTED = 46;
export function QuizOverlay({
  onClose,
  previewState
}: QuizOverlayProps) {
  const [answer, setAnswer] = useState<string | null>(previewState === 'result' ? introPoll.options[2].id : null);
  const [showResult, setShowResult] = useState(previewState === 'result');
  const select = (id: string) => {
    setAnswer(id);
    window.setTimeout(() => setShowResult(true), 380);
  };
  const mine = introPoll.options.find(o => o.id === answer);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} role="dialog" aria-modal="true" aria-label="Quiz" className="absolute inset-0 z-20 flex flex-col bg-paper" data-id="element-527" data-mp="5ko9bhasqrda">
      <div className="flex items-center justify-between border-b border-line px-5 py-3" data-id="element-528" data-mp="akiyngexb8w8">
        <p className="text-[9px] uppercase tracking-micro text-blue" data-id="element-529" data-mp="p95dlh0n1ymx">
          {showResult ? 'Your answer · peer responses' : 'Quiz'}
        </p>
        <button type="button" onClick={onClose} aria-label="Close quiz" className="flex h-7 w-7 items-center justify-center rounded-full text-mut transition-colors duration-150 hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-530" data-mp="k9ap1k4c5l51">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-531" data-mp="301qe2gdqmt9d" />
        </button>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto px-5 py-5" data-id="element-532" data-mp="2v3f5e8z6iosq">
        {!showResult ? <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="flex flex-1 flex-col justify-center" data-id="element-533" data-mp="z0af4wnt2eka">
            <h3 className="font-display text-[24px] font-semibold leading-[1.1] text-navy" data-id="element-534" data-mp="18z9bpgvaqjqx">
              {unmetNeedHeadline}
            </h3>
            <p className="mt-5 text-[13px] font-medium leading-snug text-navy2" data-id="element-535" data-mp="1hymq8nya1yii">
              {introPoll.question}
            </p>
            <div className="mt-4" data-id="element-536" data-mp="1mvd5j929cq2v">
              <PollBlock poll={introPoll} selected={answer} onSelect={select} hideSummary data-id="element-537" data-mp="1rno3w7hco3tb" />
            </div>
          </motion.div> : <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="flex flex-1 flex-col" data-id="element-538" data-mp="1wlppaz290f7y">
            <p className="text-[9px] uppercase tracking-micro text-mut" data-id="element-539" data-mp="25np5jp5fvf9u">{peerLabel}</p>
            {mine && <p className="mt-1.5 text-[13px] leading-snug text-navy" data-id="element-540" data-mp="3q3dhjj7ygrtu">
                <span className="font-display text-[20px] font-bold tabular-nums text-blue" data-id="element-541" data-mp="3l5lm4f83qnkw">
                  {mine.share}%
                </span>{' '}
                of your peers answered the same
              </p>}

            <p className="mt-5 text-center font-display text-[52px] font-bold leading-none tracking-tight tabular-nums text-blue" data-id="element-542" data-mp="3c81m19lz6xbv">
              31–46%
            </p>

            <div className="mx-auto mt-5 grid w-full max-w-[220px] grid-cols-10 gap-[5px]" role="img" aria-label="Dot grid of 100 patients, with 31 to 46 shown as reporting incomplete functional improvement" data-id="element-543" data-mp="8pi4p24u5bvu">
              {DOTS.map(i => <motion.span key={i} initial={{
            backgroundColor: '#DCE8EE'
          }} animate={{
            backgroundColor: i < HIGHLIGHTED ? '#1E76CF' : '#DCE8EE'
          }} transition={{
            duration: 0.2,
            delay: i < HIGHLIGHTED ? i * 0.004 : 0,
            ease: EASE
          }} className="aspect-square w-full rounded-full" data-id="element-544" data-mp="wg409txnl3oy" />)}
            </div>

            <p className="mt-5 text-[12.5px] leading-relaxed text-navy" data-id="element-545" data-mp="779pvht2gmes">{introReward}</p>
            <p className="mt-3 font-display text-[17px] leading-snug text-blue" data-id="element-546" data-mp="uxlvun4r492q">{introBridge}</p>

            <p className="mt-auto pt-4 text-[8.5px] leading-snug text-mut" data-id="element-547" data-mp="ywmbjwrxmvli">{references.abraham}</p>

            <button type="button" onClick={onClose} className="mt-4 flex h-11 w-full items-center justify-center rounded-full bg-navy text-[13px] font-medium text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-548" data-mp="l5pvf5f706vc">
              Back to the phenotypes
            </button>
          </motion.div>}
      </div>
    </motion.div>;
}